import React from "react";
import type { ExistingWorkCode, PullPlanTaktSectionRef } from "../../../../../models";
import ActivityRow from "../ActivityRow/ActivityRow";
import AddInline from "./AddInline/AddInline";
import SelectAllCheckbox from "./SelectAllCheckbox/SelectAllCheckbox";
import DropTarget from "../DropTarget/DropTarget";
import NotCodedBucket from "../NotCodedBucket/NotCodedBucket";
import type { StagedZone } from "../UntaggedGroups/UntaggedGroups";
import { levelKey, zoneKey } from "../../state";
import type {
  ActivityCommitted,
  ActivityMove,
  ActivityPatch,
  MoveTarget,
  TaktEdits,
} from "../../state";
import type { SectionMeta, SectionTree } from "../../tree";
import UntagAllButton from "./UntagAllButton/UntagAllButton";
import DeleteNodeButton from "./DeleteNodeButton/DeleteNodeButton";
import "./SectionRow.css";

interface Props {
  scheduleId: number;
  section: PullPlanTaktSectionRef;
  /** Tagged vs untagged counts from loaded activities; absent until loaded. */
  split?: { tagged: number; untagged: number };
  expanded: boolean;
  loading: boolean;
  tree: SectionTree | null;
  colors: Record<string, string>;
  edits: TaktEdits;
  /** Folded zones + levels — one set (zone vs level keys differ by format). */
  collapsedNodes: Set<string>;
  /** Levels expanded past the compact cap (show every row). */
  expandedLevels: Set<string>;
  onToggleLevelExpand: (key: string) => void;
  /** Just-moved activities glow; the first (scrollId) scrolls into view. */
  movedIds: Set<number>;
  scrollId: number | null;
  /** Multi-select for bulk drag. */
  selectedIds: Set<number>;
  selectionCount: number;
  onToggleSelect: (id: number) => void;
  /** Select-all for a leaf's displayed rows. */
  onToggleSelectAll: (ids: number[]) => void;
  /** Full view: this section fills the editor (its own toggle, not a mode). */
  isFocused?: boolean;
  onFocusSection?: (name: string) => void;
  onExitFocus?: () => void;
  sectionOptions: string[];
  /** A/B/C actually present in a section — scopes the placement dropdowns. */
  getSectionMeta: (section: string) => SectionMeta;
  /** Picking another section in a dropdown lazy-loads it. */
  onNeedSection: (section: string) => void;
  /** Codes created earlier this session, for the per-activity work-code picker. */
  sessionCodes?: ExistingWorkCode[];
  /** Stage a set of moves (a group → board) as one undo step. */
  onMoveToBoard?: (moves: ActivityMove[]) => void;
  /** Draft re-zonings + untags so "Group untagged" votes on the user's fixes. */
  stagedZones?: StagedZone[];
  excludedIds?: number[];
  onToggleSection: (name: string) => void;
  /** Fold/unfold a zone or level by its key. */
  onToggleNode: (key: string) => void;
  onAddZone: (section: string, taktB: string) => void;
  onAddLevel: (section: string, taktB: string, taktC: string) => void;
  /** Delete a zone / level outright: untag any activities it still holds (back
   *  to the untagged rail) and remove the row itself. */
  onDeleteZone: (section: string, taktB: string) => void;
  onDeleteLevel: (section: string, taktB: string, taktC: string) => void;
  /** Untag a set of activities (a section/zone/level's rows) — keeps the
   *  zone/level structure; the container just empties. */
  onUntagIds: (ids: number[]) => void;
  /** Remove one activity from the board (untag, keep structure). */
  onRemoveActivity?: (id: number) => void;
  onRenameSection: (original: string, value: string) => void;
  onRenameZone: (section: string, taktB: string, value: string) => void;
  onRenameLevel: (section: string, taktB: string, taktC: string, value: string) => void;
  onApply: (activityId: number, patch: ActivityPatch, committed: ActivityCommitted) => void;
  onActivityDragStart: (activityId: number, committed: ActivityCommitted) => void;
  onActivityDragEnd: () => void;
  /** Drag a zone / level head to reorder it (sets Takt ordering). */
  onZoneDragStart: (section: string, taktB: string) => void;
  onLevelDragStart: (section: string, taktB: string, taktC: string) => void;
  onReorderDragEnd: () => void;
  onDropMove: (target: MoveTarget) => void;
  /** An activity is being dragged — the header refuses it (no zone to land in);
   *  only a section-reorder drag is accepted on the header. */
  activityDragging: boolean;
  onSectionDragStart: (name: string) => void;
  /** A drop on this section's header re-orders sections (activity drops are
   *  refused — a header has no zone). */
  onSectionHeadDrop: () => void;
}

/** Stop a click on an inline control (rename box, button, drag grip) from
 *  toggling the row it lives in. */
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/** Rows shown in a level before it caps + offers "Show all". */
const LEVEL_COMPACT = 5;

const SectionRow: React.FC<Props> = ({
  scheduleId,
  section,
  split,
  expanded,
  loading,
  tree,
  colors,
  edits,
  collapsedNodes,
  expandedLevels,
  onToggleLevelExpand,
  movedIds,
  scrollId,
  selectedIds,
  selectionCount,
  onToggleSelect,
  onToggleSelectAll,
  isFocused,
  onFocusSection,
  onExitFocus,
  sectionOptions,
  getSectionMeta,
  onNeedSection,
  sessionCodes,
  onMoveToBoard,
  stagedZones,
  excludedIds,
  onToggleSection,
  onToggleNode,
  onAddZone,
  onAddLevel,
  onDeleteZone,
  onDeleteLevel,
  onUntagIds,
  onRemoveActivity,
  onRenameSection,
  onRenameZone,
  onRenameLevel,
  onApply,
  onActivityDragStart,
  onActivityDragEnd,
  onZoneDragStart,
  onLevelDragStart,
  onReorderDragEnd,
  onDropMove,
  activityDragging,
  onSectionDragStart,
  onSectionHeadDrop,
}) => {
  const name = section.name;
  const nameVal = edits.sectionNames[name] ?? name;
  const [headOver, setHeadOver] = React.useState(false);
  const listClass = "takt-editor-activity-list";

  // The count links toggle the section open/closed (like the chevron). In full
  // view the section is forced open — use "Exit full view" to leave it.
  const toggleFromCount = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    if (isFocused) return;
    onToggleSection(name);
  };

  const levelIds = (levels: SectionTree["zones"][number]["levels"]): number[] => {
    const out: number[] = [];
    for (const l of levels) for (const a of l.activities) out.push(a.id);
    return out;
  };
  const sectionPlacedIds = (): number[] => {
    const out: number[] = [];
    if (tree) for (const z of tree.zones) out.push(...levelIds(z.levels));
    return out;
  };

  // A work code (with the Work Codes tab's board-wide rename overlaid). A level
  // shows only coded bars; the rest go to the section's "Not coded" rail.
  const hasCode = (a: SectionTree["untagged"][number]): boolean =>
    Boolean(((edits.activityEdits[a.id]?.code ?? a.takt_work) || "").trim());

  // The section's ZONED-but-uncoded activities, grouped by level (in board
  // order) — the section-wide "Not coded" rail lists them under little level
  // headers + offers ONE Suggest (which groups by name ACROSS levels). Level
  // comes from the tree placement, not raw takt_b, so staged moves stay correct.
  // (Un-zoned work is `tree.untagged` → the separate "No zone" rail.)
  const noCodeGroups = React.useMemo(() => {
    const out: { takt_b: string; takt_c: string; activities: SectionTree["untagged"] }[] = [];
    if (tree) {
      for (const z of tree.zones)
        for (const l of z.levels) {
          const acts = l.activities.filter((a) => !hasCode(a));
          if (acts.length) out.push({ takt_b: z.takt_b, takt_c: l.takt_c, activities: acts });
        }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, edits.activityEdits]);
  const noCodeCount = noCodeGroups.reduce((n, g) => n + g.activities.length, 0);

  const renderActivity = (a: SectionTree["untagged"][number]) => (
    <ActivityRow
      key={a.id}
      scheduleId={scheduleId}
      activity={a}
      patch={edits.activityEdits[a.id]}
      colors={colors}
      codeEdits={edits.codeEdits}
      highlighted={movedIds.has(a.id)}
      scrollHint={a.id === scrollId}
      selected={selectedIds.has(a.id)}
      selectionCount={selectionCount}
      onToggleSelect={onToggleSelect}
      sectionOptions={sectionOptions}
      getSectionMeta={getSectionMeta}
      onNeedSection={onNeedSection}
      onApply={onApply}
      onDragStart={onActivityDragStart}
      onDragEnd={onActivityDragEnd}
      onRemove={onRemoveActivity}
      sessionCodes={sessionCodes}
      sectionNames={edits.sectionNames}
      zoneNames={edits.zoneNames}
      levelNames={edits.levelNames}
    />
  );

  return (
    <div className="takt-editor-section">
      <div
        className={`takt-editor-section-head takt-editor-clickable ${
          isFocused ? "is-focused" : ""
        } ${headOver ? "is-drop-over" : ""}`}
        draggable={!isFocused}
        onClick={() => {
          if (!isFocused) onToggleSection(name);
        }}
        title={isFocused ? undefined : expanded ? "Collapse section" : "Expand section"}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", `section:${name}`);
          onSectionDragStart(name);
        }}
        onDragOver={(e) => {
          // A header has no zone, so it can't accept an activity — show "no-drop"
          // and don't mark it a drop target (only section reordering is allowed).
          if (activityDragging) {
            e.dataTransfer.dropEffect = "none";
            return;
          }
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          if (!headOver) setHeadOver(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setHeadOver(false);
        }}
        onDrop={(e) => {
          if (activityDragging) return;
          e.preventDefault();
          setHeadOver(false);
          onSectionHeadDrop();
        }}
      >
        {!isFocused && (
          <span className="takt-editor-grip" onClick={stop} title="Drag to reorder" aria-hidden>
            ⠿
          </span>
        )}
        {!isFocused && (
          <span className="takt-editor-chevron">{expanded ? "▾" : "▸"}</span>
        )}
        <input
          className="takt-editor-name-input takt-editor-name-input--section"
          value={nameVal}
          size={Math.max(nameVal.length, 3)}
          draggable={false}
          onClick={stop}
          onChange={(e) => onRenameSection(name, e.target.value)}
          aria-label="Section name"
          title="Click to rename section"
        />
        {onFocusSection &&
          (isFocused ? (
            <button
              type="button"
              className="takt-editor-fullview-btn is-active"
              onClick={(e) => {
                stop(e);
                onExitFocus?.();
              }}
              title="Back to all sections"
            >
              ⤢ Exit full view
            </button>
          ) : (
            <button
              type="button"
              className="takt-editor-fullview-btn"
              onClick={(e) => {
                stop(e);
                onFocusSection(name);
              }}
              title="Expand this section to fill the editor"
            >
              ⤢ Full view
            </button>
          ))}
        <span className="takt-editor-count takt-editor-count--section">
          {split ? (
            <>
              <button type="button" className="takt-editor-count-link" onClick={toggleFromCount}>
                {split.tagged} Tagged Activities
              </button>
              <span className="takt-editor-count-sep" aria-hidden>
                ·
              </span>
              <button type="button" className="takt-editor-count-link" onClick={toggleFromCount}>
                {split.untagged} no work code
              </button>
            </>
          ) : (
            `${section.activity_count} activities`
          )}
        </span>
        {tree && (
          <UntagAllButton
            count={sectionPlacedIds().length}
            scope="section"
            onConfirm={() => onUntagIds(sectionPlacedIds())}
          />
        )}
      </div>

      {expanded && (
        <div className="takt-editor-section-body">
          {loading && <div className="takt-editor-hint">Loading…</div>}
          {!loading && tree && (
            <div
              className={`takt-editor-section-cols ${
                noCodeCount > 0 || tree.untagged.length > 0 ? "has-rail" : ""
              }`}
            >
              <div className="takt-editor-section-structure">
                <AddInline
                  label="+ Zone"
                  placeholder="New zone (Takt B)…"
                  onAdd={(v) => onAddZone(name, v)}
                />
                {tree.zones.map((zone) => {
                  const zoneOpen = !collapsedNodes.has(zoneKey(name, zone.takt_b));
                  return (
                    <div key={zone.takt_b} className="takt-editor-zone">
                  <DropTarget
                    className="takt-editor-zone-head takt-editor-clickable"
                    target={{ section: name, takt_a: section.takt_a, takt_b: zone.takt_b, takt_c: "" }}
                    onDropMove={onDropMove}
                    onClick={() => onToggleNode(zoneKey(name, zone.takt_b))}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", `zone:${zone.takt_b}`);
                      onZoneDragStart(name, zone.takt_b);
                    }}
                    onDragEnd={onReorderDragEnd}
                  >
                    <span className="takt-editor-grip" onClick={stop} title="Drag to reorder" aria-hidden>
                      ⠿
                    </span>
                    <span className="takt-editor-chevron">{zoneOpen ? "▾" : "▸"}</span>
                    <input
                      className="takt-editor-name-input takt-editor-name-input--zone"
                      value={edits.zoneNames[zoneKey(name, zone.takt_b)] ?? zone.takt_b}
                      draggable={false}
                      onClick={stop}
                      onChange={(e) => onRenameZone(name, zone.takt_b, e.target.value)}
                      aria-label="Zone name"
                      title="Click to rename zone"
                    />
                    <UntagAllButton
                      count={levelIds(zone.levels).length}
                      scope="zone"
                      onConfirm={() => onUntagIds(levelIds(zone.levels))}
                    />
                    <DeleteNodeButton
                      count={levelIds(zone.levels).length}
                      scope="zone"
                      onConfirm={() => onDeleteZone(name, zone.takt_b)}
                    />
                  </DropTarget>
                  {zoneOpen && (
                    <AddInline
                      small
                      label="+ Level"
                      placeholder="New level (Takt C)…"
                      onAdd={(v) => onAddLevel(name, zone.takt_b, v)}
                    />
                  )}
                  {zoneOpen &&
                    zone.levels.map((level) => {
                      const lk = levelKey(name, zone.takt_b, level.takt_c);
                      const open = !collapsedNodes.has(lk);
                      const big = expandedLevels.has(lk);
                      // The level shows only its board bars (coded). Uncoded work
                      // lives in the section's "Not coded" rail, grouped by level.
                      const codedActs = level.activities.filter(hasCode);
                      return (
                        <DropTarget
                          key={level.takt_c}
                          className="takt-editor-level"
                          target={{
                            section: name,
                            takt_a: section.takt_a,
                            takt_b: zone.takt_b,
                            takt_c: level.takt_c,
                          }}
                          onDropMove={onDropMove}
                        >
                          <div
                            className="takt-editor-level-head takt-editor-clickable"
                            onClick={() => onToggleNode(lk)}
                            draggable
                            onDragStart={(e) => {
                              e.dataTransfer.effectAllowed = "move";
                              e.dataTransfer.setData("text/plain", `level:${level.takt_c}`);
                              onLevelDragStart(name, zone.takt_b, level.takt_c);
                            }}
                            onDragEnd={onReorderDragEnd}
                          >
                            <SelectAllCheckbox
                              ids={level.activities.map((a) => a.id)}
                              selectedIds={selectedIds}
                              onToggle={onToggleSelectAll}
                            />
                            <span
                              className="takt-editor-grip"
                              onClick={stop}
                              title="Drag to reorder"
                              aria-hidden
                            >
                              ⠿
                            </span>
                            <span className="takt-editor-chevron">{open ? "▾" : "▸"}</span>
                            <input
                              className="takt-editor-name-input takt-editor-name-input--level"
                              value={edits.levelNames[lk] ?? level.takt_c}
                              draggable={false}
                              onClick={stop}
                              onChange={(e) =>
                                onRenameLevel(name, zone.takt_b, level.takt_c, e.target.value)
                              }
                              aria-label="Level name"
                              title="Click to rename level"
                            />
                            <span className="takt-editor-count">{level.activities.length}</span>
                            <UntagAllButton
                              count={level.activities.length}
                              scope="level"
                              onConfirm={() => onUntagIds(level.activities.map((a) => a.id))}
                            />
                            <DeleteNodeButton
                              count={level.activities.length}
                              scope="level"
                              onConfirm={() => onDeleteLevel(name, zone.takt_b, level.takt_c)}
                            />
                          </div>
                          {open &&
                            (codedActs.length > 0 ? (
                              <>
                                <div className={`${listClass} ${big ? "is-expanded" : ""}`}>
                                  {codedActs.map(renderActivity)}
                                </div>
                                {codedActs.length > LEVEL_COMPACT && (
                                  <button
                                    type="button"
                                    className="takt-editor-showmore"
                                    onClick={(e) => {
                                      stop(e);
                                      onToggleLevelExpand(lk);
                                    }}
                                  >
                                    {big ? "Show less ▴" : `Show all ${codedActs.length} ▾`}
                                  </button>
                                )}
                              </>
                            ) : (
                              <div className="takt-editor-empty-drop">Drop activities here</div>
                            ))}
                        </DropTarget>
                      );
                    })}
                    </div>
                  );
                })}
              </div>
              {(noCodeCount > 0 || tree.untagged.length > 0) && (
                <div className="takt-editor-untagged-rail">
                  {onMoveToBoard && noCodeCount > 0 && (
                    <NotCodedBucket
                      groups={noCodeGroups}
                      renderActivity={renderActivity}
                      scheduleId={scheduleId}
                      sectionName={name}
                      sessionCodes={sessionCodes}
                      sectionOptions={sectionOptions}
                      getSectionMeta={getSectionMeta}
                      onNeedSection={onNeedSection}
                      onMoveToBoard={onMoveToBoard}
                      colors={colors}
                      codeEdits={edits.codeEdits}
                      stagedZones={stagedZones}
                      excludedIds={excludedIds}
                    />
                  )}
                  {tree.untagged.length > 0 && (
                    <div className="takt-editor-untagged">
                      <div className="takt-editor-untagged-head">
                        <SelectAllCheckbox
                          ids={tree.untagged.map((a) => a.id)}
                          selectedIds={selectedIds}
                          onToggle={onToggleSelectAll}
                        />
                        <span title="No zone yet — map this work's WBS leaf in the WBS Mapping tab.">
                          No zone
                        </span>
                        <span className="takt-editor-count">{tree.untagged.length}</span>
                      </div>
                      <div className={listClass}>{tree.untagged.map(renderActivity)}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionRow;
